/**
 * CompanySettingsView — הגדרות חברה
 *
 * Phase 15: Replaces the single "current_vat_rate" input with a proper
 * VAT-history ledger table backed by `company_vat_history`.
 *
 * The "active" rate at any moment is the row with the latest
 * `effective_from` date that is ≤ TODAY.
 *
 * UI features:
 *   • Data table listing all VAT rate entries (rate %, effective-from date,
 *     actions: edit / delete).
 *   • Add-row button opens an inline modal: rate input + date picker.
 *   • Edit opens the same modal pre-filled.
 *   • Delete shows a confirmation modal.
 *   • "Active" badge is shown on the currently effective row.
 *
 * Architecture note (VAT snapshot):
 *   All monetary values in inventory_products.price / .cost and in
 *   inventory_product_prices.specific_price are stored EXCLUDING VAT.
 *   When generating order / invoice documents the system MUST:
 *     1. SNAPSHOT the rate active on the document date (query
 *        company_vat_history WHERE effective_from <= :doc_date ORDER BY
 *        effective_from DESC LIMIT 1) into each document line so that
 *        historical documents stay correct after future rate changes.
 *     2. RESPECT inventory_clients.is_vat_liable — if false, use 0% VAT
 *        regardless of the company-wide rate.
 *     3. NEVER recompute historical totals from the live rate row.
 */

import { useEffect, useState, useCallback } from 'react';
import { Building2, Percent, Loader as Loader2, CircleAlert as AlertCircle, CircleCheck as CheckCircle2, Plus, Pencil, Trash2, X, Save, Info, Receipt, Calendar, ShoppingCart, ToggleLeft, ToggleRight, Boxes } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { B2BTermsSettings } from '@/components/inventory/B2BTermsSettings';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface VatHistoryRow {
  id:             string;
  company_id:     string;
  rate:           number;
  effective_from: string;   // ISO date string "YYYY-MM-DD"
  created_at:     string;
}

interface Props {
  companyId: string;
  showToast: (msg: string, ok?: boolean) => void;
}

// ─── B2B Backorder Toggle ─────────────────────────────────────────────────────

function B2BBackorderToggle({
  companyId, showToast,
}: {
  companyId: string;
  showToast: (msg: string, ok?: boolean) => void;
}) {
  const [enabled,  setEnabled]  = useState<boolean>(false);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);

  // Fetch current value
  useEffect(() => {
    if (!companyId) return;
    supabase
      .from('companies')
      .select('allow_b2b_backorders')
      .eq('id', companyId)
      .single()
      .then(({ data, error }) => {
        if (!error && data) {
          setEnabled(Boolean((data as { allow_b2b_backorders: boolean }).allow_b2b_backorders));
        }
        setLoading(false);
      });
  }, [companyId]);

  const handleToggle = async () => {
    const next = !enabled;
    setEnabled(next);   // optimistic
    setSaving(true);
    const { error } = await supabase
      .from('companies')
      .update({ allow_b2b_backorders: next })
      .eq('id', companyId);
    setSaving(false);
    if (error) {
      setEnabled(!next); // rollback
      showToast(error.message, false);
    } else {
      showToast(
        next
          ? 'הזמנות B2B מאפס מלאי: מופעל לכל המוצרים'
          : 'הזמנות B2B מאפס מלאי: מכובה (ברירת מחדל)',
      );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-3 text-sm text-gray-400">
        <Loader2 className="w-4 h-4 animate-spin" />
        טוען הגדרות…
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={handleToggle}
      disabled={saving}
      className={`w-full flex items-center justify-between gap-4 px-5 py-4 rounded-xl
                  border-2 transition-all text-right
                  ${enabled
                    ? 'border-emerald-400 bg-emerald-50 hover:bg-emerald-100'
                    : 'border-gray-200 bg-gray-50  hover:bg-gray-100'}
                  disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      {/* Left: status indicator */}
      <div className="flex items-center gap-2 shrink-0">
        {saving ? (
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        ) : enabled ? (
          <ToggleRight className="w-6 h-6 text-emerald-500" />
        ) : (
          <ToggleLeft  className="w-6 h-6 text-gray-400" />
        )}
        <span className={`text-sm font-bold ${
          enabled ? 'text-emerald-700' : 'text-gray-500'
        }`}>
          {enabled ? 'מופעל' : 'כבוי'}
        </span>
      </div>

      {/* Right: label */}
      <div className="flex-1 min-w-0 text-right">
        <p className="text-sm font-semibold text-gray-800">
          אפשר הזמנות B2B כאשר המלאי אפס / שלילי
        </p>
        <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
          {enabled
            ? 'לקוחות B2B יכולים להזמין מוצרים גם כשאין מלאי זמין — אלא אם מוצר ספציפי מוגדר אחרת'
            : 'מוצרים ללא מלאי זמין מוצגים כ"אזל מהמלאי" ואינם ניתנים להזמנה — אלא אם מוצר ספציפי מוגדר אחרת'}
        </p>
      </div>
    </button>
  );
}

// ─── B2B Returns Toggle ──────────────────────────────────────────────────────

function B2BReturnsToggle({
  companyId, showToast,
}: {
  companyId: string;
  showToast: (msg: string, ok?: boolean) => void;
}) {
  const [enabled,  setEnabled]  = useState<boolean>(true);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);

  useEffect(() => {
    if (!companyId) return;
    supabase
      .from('companies')
      .select('b2b_returns_enabled')
      .eq('id', companyId)
      .single()
      .then(({ data, error }) => {
        if (!error && data) {
          setEnabled(Boolean((data as { b2b_returns_enabled: boolean }).b2b_returns_enabled));
        }
        setLoading(false);
      });
  }, [companyId]);

  const handleToggle = async () => {
    const next = !enabled;
    setEnabled(next);
    setSaving(true);
    const { error } = await supabase
      .from('companies')
      .update({ b2b_returns_enabled: next })
      .eq('id', companyId);
    setSaving(false);
    if (error) {
      setEnabled(!next);
      showToast(error.message, false);
    } else {
      showToast(
        next
          ? 'החזרות B2B: מופעל — לקוחות יכולים לבקש החזרות'
          : 'החזרות B2B: כבוי — לקוחות לא יראו את עמודי ההחזרה',
      );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-3 text-sm text-gray-400">
        <Loader2 className="w-4 h-4 animate-spin" />
        טוען הגדרות…
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={handleToggle}
      disabled={saving}
      className={`w-full flex items-center justify-between gap-4 px-5 py-4 rounded-xl
                  border-2 transition-all text-right
                  ${enabled
                    ? 'border-orange-400 bg-orange-50 hover:bg-orange-100'
                    : 'border-gray-200 bg-gray-50  hover:bg-gray-100'}
                  disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      <div className="flex items-center gap-2 shrink-0">
        {saving ? (
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        ) : enabled ? (
          <ToggleRight className="w-6 h-6 text-orange-500" />
        ) : (
          <ToggleLeft  className="w-6 h-6 text-gray-400" />
        )}
        <span className={`text-sm font-bold ${
          enabled ? 'text-orange-700' : 'text-gray-500'
        }`}>
          {enabled ? 'מופעל' : 'כבוי'}
        </span>
      </div>

      <div className="flex-1 min-w-0 text-right">
        <p className="text-sm font-semibold text-gray-800">
          אפשר ללקוחות B2B לבקש החזרות
        </p>
        <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
          {enabled
            ? 'לקוחות B2B רואים את עמוד ההחזרות ויכולים לבקש החזרה על הזמנות שנמסרו'
            : 'עמוד ההחזרות מוסתר מלקוחות B2B — לא ניתן לבקש החזרות'}
        </p>
      </div>
    </button>
  );
}

// ─── Consignment Sales Toggle ─────────────────────────────────────────────────

function ConsignmentSalesToggle({
  companyId, showToast,
}: {
  companyId: string;
  showToast: (msg: string, ok?: boolean) => void;
}) {
  const [enabled,     setEnabled]     = useState(false);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    supabase
      .from('companies')
      .select('allow_consignment_sales')
      .eq('id', companyId)
      .single()
      .then(({ data, error }) => {
        if (!error && data) {
          setEnabled(Boolean((data as { allow_consignment_sales: boolean }).allow_consignment_sales));
        }
        setLoading(false);
      });
  }, [companyId]);

  // Called when user clicks the toggle button
  const handleClick = () => {
    if (!enabled) {
      // Enabling — show confirmation modal first
      setShowConfirm(true);
    } else {
      // Disabling — apply immediately
      applyToggle(false);
    }
  };

  const applyToggle = async (next: boolean) => {
    setSaving(true);
    setShowConfirm(false);

    // 1. Update the flag on the company
    const { error } = await supabase
      .from('companies')
      .update({ allow_consignment_sales: next })
      .eq('id', companyId);

    if (error) {
      setSaving(false);
      showToast(error.message, false);
      return;
    }

    // 2. If enabling, seed the two consignment document types
    if (next) {
      const { error: rpcErr } = await supabase
        .rpc('seed_consignment_doc_types', { p_company_id: companyId });
      if (rpcErr) {
        // Non-fatal: flag is already set, doc types may already exist
        showToast(`ההגדרה הופעלה אך אירעה שגיאה בהוספת תעודות: ${rpcErr.message}`, false);
        setSaving(false);
        setEnabled(true);
        return;
      }
    }

    setSaving(false);
    setEnabled(next);
    showToast(
      next
        ? 'מכירה בקונסגנציה: הופעלה — תעודות הקונסגנציה נוספו להגדרות המסמכים'
        : 'מכירה בקונסגנציה: כובתה — לקוחות ומחסנות קונסגנציה לא ניתן להגדיר',
    );
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-3 text-sm text-gray-400">
        <Loader2 className="w-4 h-4 animate-spin" />
        טוען הגדרות…
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        <button
          type="button"
          onClick={handleClick}
          disabled={saving}
          className={`w-full flex items-center justify-between gap-4 px-5 py-4 rounded-xl
                      border-2 transition-all text-right
                      ${enabled
                        ? 'border-teal-400 bg-teal-50 hover:bg-teal-100'
                        : 'border-gray-200 bg-gray-50  hover:bg-gray-100'}
                      disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          <div className="flex items-center gap-2 shrink-0">
            {saving ? (
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            ) : enabled ? (
              <ToggleRight className="w-6 h-6 text-teal-500" />
            ) : (
              <ToggleLeft  className="w-6 h-6 text-gray-400" />
            )}
            <span className={`text-sm font-bold ${enabled ? 'text-teal-700' : 'text-gray-500'}`}>
              {enabled ? 'מופעל' : 'כבוי'}
            </span>
          </div>
          <div className="flex-1 min-w-0 text-right">
            <p className="text-sm font-semibold text-gray-800">
              אפשר מכירה בקונסגנציה לחברה זו
            </p>
            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
              {enabled
                ? 'ניתן להגדיר לקוחות קונסגנציה ולפתוח מחסנות קונסגנציה — החנות מחזיקה את הסחורה ומשלמת רק על מה שנמכר'
                : 'הגדרת לקוחות ומחסנות קונסגנציה חסומה — הפעל כדי לעבוד במודל קונסגנציה'}
            </p>
          </div>
        </button>

        {!enabled && (
          <div className="flex items-start gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-amber-500" />
            <span>
              כל עוד הגדרה זו כבויה, לא ניתן להגדיר לקוח כלקוח קונסגנציה ולא ניתן לפתוח מחסן קונסגנציה.
            </span>
          </div>
        )}
      </div>

      {/* ── Confirmation Modal ─────────────────────────────────────────────── */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
             style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" dir="rtl">

            {/* Header */}
            <div className="flex items-center gap-3 px-6 py-5 border-b border-gray-100">
              <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center shrink-0">
                <Boxes className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 text-base">הפעלת מודל קונסגנציה</h3>
                <p className="text-xs text-gray-500 mt-0.5">המערכת תבצע את הפעולות הבאות</p>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-gray-700 leading-relaxed">
                על מנת להפעיל את מודל הקונסגנציה, המערכת תוסיף אוטומטית שלושה סוגי מסמכים
                לחברה זו:
              </p>

              <div className="space-y-2">
                <div className="flex items-center gap-3 px-4 py-3 bg-teal-50 border border-teal-200 rounded-xl">
                  <span className="px-2 py-0.5 bg-teal-100 text-teal-700 text-xs font-bold rounded font-mono">DNCO</span>
                  <div>
                    <p className="text-sm font-semibold text-teal-900">תעודת משלוח קונסגנציה</p>
                    <p className="text-xs text-teal-600">משלוח סחורה לחנות — מעדכן מלאי, חייב במע"מ</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 px-4 py-3 bg-teal-50 border border-teal-200 rounded-xl">
                  <span className="px-2 py-0.5 bg-teal-100 text-teal-700 text-xs font-bold rounded font-mono">RTVCO</span>
                  <div>
                    <p className="text-sm font-semibold text-teal-900">תעודת החזרה מלקוח קונסגנציה</p>
                    <p className="text-xs text-teal-600">החזרת סחורה שלא נמכרה — מעדכן מלאי, חייב במע"מ</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 px-4 py-3 bg-teal-50 border border-teal-200 rounded-xl">
                  <span className="px-2 py-0.5 bg-teal-100 text-teal-700 text-xs font-bold rounded font-mono">WHTCO</span>
                  <div>
                    <p className="text-sm font-semibold text-teal-900">תעודת העברה בין מחסנים קונסגנציה</p>
                    <p className="text-xs text-teal-600">העברת סחורה בין מחסנים בתוך מודל הקונסגנציה — מעדכן מלאי, חייב במע"מ</p>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-2 px-3 py-2.5 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
                <Info className="w-4 h-4 shrink-0 mt-0.5 text-blue-500" />
                <span>
                  המסמכים יופיעו בעמוד <strong>הפקת מסמך חדש</strong> ובהגדרות המסמכים.
                  מספור ראשוני: 1001 (ניתן לשינוי בהגדרות מסמכים לאחר ההפעלה).
                </span>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800
                           border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                ביטול
              </button>
              <button
                onClick={() => applyToggle(true)}
                className="flex items-center gap-2 px-5 py-2 bg-teal-600 hover:bg-teal-700
                           text-white text-sm font-semibold rounded-lg transition-colors"
              >
                <Boxes className="w-4 h-4" />
                הפעל קונסגנציה
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Exported helper: fetch consignment flag ───────────────────────────────────

export async function fetchCompanyConsignmentEnabled(companyId: string): Promise<boolean> {
  const { data } = await supabase
    .from('companies')
    .select('allow_consignment_sales')
    .eq('id', companyId)
    .maybeSingle();
  return Boolean(data && (data as { allow_consignment_sales: boolean }).allow_consignment_sales);
}

// ─────────────────────────────────────────────────────────────────────────────

interface RowForm {
  rate:           string;   // string while editing
  effective_from: string;   // "YYYY-MM-DD"
}

const EMPTY_FORM: RowForm = { rate: '17', effective_from: '' };
const DEFAULT_VAT = 17;

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Returns today's date as "YYYY-MM-DD" in local time */
function todayStr(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm   = String(d.getMonth() + 1).padStart(2, '0');
  const dd   = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** Format a "YYYY-MM-DD" string for Hebrew display */
function formatDate(iso: string): string {
  if (!iso) return '—';
  const [y, m, day] = iso.split('-');
  return `${day}/${m}/${y}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function CompanySettingsView({ companyId, showToast }: Props) {

  // ── State ──────────────────────────────────────────────────────────────────
  const [rows,       setRows]       = useState<VatHistoryRow[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState<string | null>(null);

  // Modal state
  const [modalOpen,  setModalOpen]  = useState(false);
  const [editingRow, setEditingRow] = useState<VatHistoryRow | null>(null);
  const [form,       setForm]       = useState<RowForm>(EMPTY_FORM);
  const [formErr,    setFormErr]    = useState<string | null>(null);
  const [saving,     setSaving]     = useState(false);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<VatHistoryRow | null>(null);
  const [deleting,     setDeleting]     = useState(false);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchRows = useCallback(async () => {
    setLoading(true); setError(null);
    const { data, error: err } = await supabase
      .from('company_vat_history')
      .select('id, company_id, rate, effective_from, created_at')
      .eq('company_id', companyId)
      .order('effective_from', { ascending: false });

    if (err) { setError(err.message); setLoading(false); return; }
    setRows((data ?? []) as VatHistoryRow[]);
    setLoading(false);
  }, [companyId]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // ── Derived: which row is currently "active"? ─────────────────────────────
  const today = todayStr();
  const activeRow = rows.find(r => r.effective_from <= today) ?? null;

  // ── Modal helpers ──────────────────────────────────────────────────────────
  const openAdd = () => {
    setEditingRow(null);
    setForm({ rate: '17', effective_from: today });
    setFormErr(null);
    setModalOpen(true);
  };

  const openEdit = (row: VatHistoryRow) => {
    setEditingRow(row);
    setForm({ rate: String(row.rate), effective_from: row.effective_from });
    setFormErr(null);
    setModalOpen(true);
  };

  const closeModal = () => { setModalOpen(false); setFormErr(null); };

  // ── Validation ─────────────────────────────────────────────────────────────
  const validate = (): string | null => {
    const r = parseFloat(form.rate);
    if (isNaN(r) || r < 0 || r > 100) return 'אחוז מע"מ חייב להיות בין 0 ל-100';
    if (!form.effective_from) return 'תאריך תחולה הוא שדה חובה';
    return null;
  };

  // ── Save (insert / update) ─────────────────────────────────────────────────
  const handleSave = async () => {
    const err = validate();
    if (err) { setFormErr(err); return; }
    setSaving(true); setFormErr(null);

    const payload = {
      company_id:     companyId,
      rate:           parseFloat(parseFloat(form.rate).toFixed(2)),
      effective_from: form.effective_from,
    };

    if (editingRow) {
      const { error: upErr } = await supabase
        .from('company_vat_history')
        .update(payload)
        .eq('id', editingRow.id);
      if (upErr) {
        setFormErr(upErr.message);
        setSaving(false);
        return;
      }
      showToast('שיעור המע"מ עודכן בהצלחה');
    } else {
      const { error: inErr } = await supabase
        .from('company_vat_history')
        .insert(payload);
      if (inErr) {
        // Unique-constraint violation → friendly message
        if (inErr.message.includes('uq_vat_history_company_date')) {
          setFormErr('כבר קיים רשומה לתאריך זה — ערוך את הרשומה הקיימת');
        } else {
          setFormErr(inErr.message);
        }
        setSaving(false);
        return;
      }
      showToast('שיעור מע"מ חדש נוסף בהצלחה');
    }

    setSaving(false);
    closeModal();
    fetchRows();
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const { error: delErr } = await supabase
      .from('company_vat_history')
      .delete()
      .eq('id', deleteTarget.id);
    if (delErr) {
      showToast(delErr.message, false);
    } else {
      showToast('הרשומה נמחקה');
      fetchRows();
    }
    setDeleting(false);
    setDeleteTarget(null);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">

      {/* ── Card: B2B Backorder Policy ─────────────────────────────────────── */}
      <section className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
          <ShoppingCart className="w-5 h-5 text-primary-500" />
          <div>
            <h2 className="text-base font-semibold text-gray-900">הגדרות B2B — מדיניות מלאי</h2>
            <p className="text-xs text-gray-500">
              שלוט בהזמנות מאפס מלאי עבור כלל המוצרים בחברה זו
            </p>
          </div>
        </div>
        <div className="px-6 py-5 space-y-3">
          <B2BBackorderToggle companyId={companyId} showToast={showToast} />
          <p className="text-xs text-gray-400 leading-relaxed">
            <strong className="text-gray-600">ירושת הגדרות:</strong>{' '}
            הגדרה זו חלה על כל המוצרים. ניתן לדרוס אותה ברמת מוצר בודד בעמוד{' '}
            <em>ניהול מוצרים → ערוך מוצר → מדיניות B2B</em>.
          </p>
        </div>
      </section>

      {/* ── Card: B2B Returns Policy ─────────────────────────────────────── */}
      <section className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
          <Receipt className="w-5 h-5 text-orange-500" />
          <div>
            <h2 className="text-base font-semibold text-gray-900">הגדרות B2B — החזרות</h2>
            <p className="text-xs text-gray-500">
              שלוט בנראות עמודי ההחזרה עבור לקוחות B2B בחברה זו
            </p>
          </div>
        </div>
        <div className="px-6 py-5 space-y-3">
          <B2BReturnsToggle companyId={companyId} showToast={showToast} />
          <p className="text-xs text-gray-400 leading-relaxed">
            <strong className="text-gray-600">השפעה:</strong>{' '}
            כשמכובה — לקוחות B2B לא רואים את תפריט ההחזרות ולא יכולים לבקש החזרה מהזמנות.
            הגדרה זו חלה ברמת החברה ומשפיעה על כל הלקוחות.
          </p>
        </div>
      </section>

      <B2BTermsSettings companyId={companyId} showToast={showToast} />

      {/* ── Card: Consignment Sales ────────────────────────────────────────── */}
      <section className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
          <Boxes className="w-5 h-5 text-teal-500" />
          <div>
            <h2 className="text-base font-semibold text-gray-900">הגדרות קונסגנציה</h2>
            <p className="text-xs text-gray-500">
              הפעל אם החברה מוכרת סחורה בקונסגנציה — המחסן מחזיק סחורה וגובה תשלום רק על מה שנמכר
            </p>
          </div>
        </div>
        <div className="px-6 py-5">
          <ConsignmentSalesToggle companyId={companyId} showToast={showToast} />
        </div>
      </section>

      {/* ── Card: VAT History ──────────────────────────────────────────────── */}
      <section className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <Building2 className="w-5 h-5 text-orange-500" />
            <div>
              <h2 className="text-base font-semibold text-gray-900">היסטוריית שיעורי מע"מ</h2>
              <p className="text-xs text-gray-500">
                השיעור הפעיל הוא הרשומה עם תאריך התחולה האחרון שאינו עתידי
              </p>
            </div>
          </div>
          <button
            onClick={openAdd}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700
                       text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            הוסף שיעור
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-3 mx-6 mt-4 px-4 py-3 bg-red-50 border
                          border-red-200 rounded-xl text-sm text-red-700">
            <AlertCircle className="w-4 h-4 shrink-0" />{error}
          </div>
        )}

        {/* Table body */}
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">טוען נתונים…</span>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 gap-3 text-gray-400">
            <Percent className="w-9 h-9 opacity-30" />
            <p className="text-sm">אין רשומות. הוסף את שיעור המע"מ הראשון.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-6 py-3 text-right font-semibold text-gray-600 text-xs uppercase tracking-wide">
                    שיעור מע"מ
                  </th>
                  <th className="px-6 py-3 text-right font-semibold text-gray-600 text-xs uppercase tracking-wide">
                    תאריך תחולה
                  </th>
                  <th className="px-6 py-3 text-right font-semibold text-gray-600 text-xs uppercase tracking-wide">
                    סטטוס
                  </th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {rows.map(row => {
                  const isActive  = activeRow?.id === row.id;
                  const isFuture  = row.effective_from > today;
                  return (
                    <tr key={row.id} className="hover:bg-gray-50/60 transition-colors">
                      {/* Rate */}
                      <td className="px-6 py-3.5">
                        <span className="font-semibold text-gray-900">{row.rate}%</span>
                      </td>
                      {/* Effective from */}
                      <td className="px-6 py-3.5 text-gray-600 flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-gray-400" />
                        {formatDate(row.effective_from)}
                      </td>
                      {/* Status badge */}
                      <td className="px-6 py-3.5">
                        {isActive ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1
                                           bg-green-50 border border-green-200 rounded-full
                                           text-xs font-medium text-green-700">
                            <CheckCircle2 className="w-3 h-3" />
                            פעיל
                          </span>
                        ) : isFuture ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1
                                           bg-blue-50 border border-blue-200 rounded-full
                                           text-xs font-medium text-blue-600">
                            עתידי
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1
                                           bg-gray-50 border border-gray-200 rounded-full
                                           text-xs font-medium text-gray-500">
                            היסטורי
                          </span>
                        )}
                      </td>
                      {/* Actions */}
                      <td className="px-6 py-3.5">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => openEdit(row)}
                            className="p-1.5 rounded-lg hover:bg-blue-50 text-gray-400
                                       hover:text-blue-600 transition-colors"
                            title="ערוך"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeleteTarget(row)}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400
                                       hover:text-red-600 transition-colors"
                            title="מחק"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── Architecture / info panel ───────────────────────────────────────── */}
      <section className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
          <Info className="w-4 h-4 text-blue-500" />
          <h3 className="text-sm font-semibold text-gray-700">הערות ארכיטקטורה — אחסון מחירים ומע"מ</h3>
        </div>
        <div className="px-6 py-5">
          <div className="bg-blue-50 border border-blue-100 rounded-xl px-5 py-4 space-y-3
                          text-xs text-blue-800 leading-relaxed">
            <div className="flex gap-2.5">
              <Receipt className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
              <p>
                <strong>כל המחירים מאוחסנים ללא מע"מ</strong> —
                עמודות <code className="bg-blue-100 px-1 rounded">price</code>,{' '}
                <code className="bg-blue-100 px-1 rounded">cost</code> ב-<code className="bg-blue-100 px-1 rounded">inventory_products</code>{' '}
                ו-<code className="bg-blue-100 px-1 rounded">specific_price</code> ב-<code className="bg-blue-100 px-1 rounded">inventory_product_prices</code>{' '}
                מייצגות מחיר ללא מע"מ. הממשק מציג שני שדות לנוחות המשתמש.
              </p>
            </div>
            <div className="flex gap-2.5">
              <Receipt className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
              <p>
                <strong>Snapshot בעת יצירת מסמך</strong> —
                בעת יצירת הזמנה / חשבונית, שאל את השיעור הפעיל ב<em>תאריך המסמך</em>:
                <code className="block bg-blue-100 mt-1 px-2 py-1 rounded font-mono text-[11px]">
                  SELECT rate FROM company_vat_history
                  WHERE company_id = :cid AND effective_from &lt;= :doc_date
                  ORDER BY effective_from DESC LIMIT 1
                </code>
                שמור את הערך ב-<code className="bg-blue-100 px-1 rounded">order_lines.vat_rate</code>.
              </p>
            </div>
            <div className="flex gap-2.5">
              <Receipt className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
              <p>
                <strong>לקוח פטור ממע"מ</strong> —
                אם <code className="bg-blue-100 px-1 rounded">inventory_clients.is_vat_liable = false</code>,
                החל שיעור מע"מ של 0% ללא קשר לטבלה. לעולם אל תחשב מחדש מסמכים היסטוריים.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Add / Edit Modal ────────────────────────────────────────────────── */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
             style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">

            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900 text-base">
                {editingRow ? 'עריכת שיעור מע"מ' : 'הוסף שיעור מע"מ חדש'}
              </h3>
              <button onClick={closeModal}
                      className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal body */}
            <div className="px-6 py-5 space-y-4">

              {/* Form error */}
              {formErr && (
                <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border
                                border-red-200 rounded-lg text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {formErr}
                </div>
              )}

              {/* Rate input */}
              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                  <Percent className="w-4 h-4 text-orange-500" />
                  שיעור מע"מ (%)
                  <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={form.rate}
                    onChange={e => setForm(f => ({ ...f, rate: e.target.value }))}
                    dir="ltr"
                    className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm
                               focus:outline-none focus:ring-2 focus:ring-orange-400
                               focus:border-transparent"
                    placeholder="17.00"
                  />
                  <Percent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4
                                      text-gray-400 pointer-events-none" />
                </div>
                <p className="text-xs text-gray-400">לדוגמה: 17 לשיעור הסטנדרטי הישראלי</p>
              </div>

              {/* Effective from date */}
              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                  <Calendar className="w-4 h-4 text-orange-500" />
                  תאריך תחולה
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={form.effective_from}
                  onChange={e => setForm(f => ({ ...f, effective_from: e.target.value }))}
                  dir="ltr"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                             focus:outline-none focus:ring-2 focus:ring-orange-400
                             focus:border-transparent"
                />
                <p className="text-xs text-gray-400">
                  מהתאריך הזה ואילך שיעור זה יהיה הפעיל
                </p>
              </div>
            </div>

            {/* Modal footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button
                onClick={closeModal}
                disabled={saving}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800
                           border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                ביטול
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700
                           disabled:opacity-50 disabled:cursor-not-allowed
                           text-white text-sm font-medium rounded-lg transition-colors"
              >
                {saving
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Save className="w-4 h-4" />}
                {editingRow ? 'שמור שינויים' : 'הוסף שיעור'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation Modal ───────────────────────────────────────── */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
             style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="px-6 py-5 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">מחיקת שיעור מע"מ</h3>
                  <p className="text-xs text-gray-500 mt-0.5">פעולה זו אינה הפיכה</p>
                </div>
              </div>
              <p className="text-sm text-gray-700">
                האם למחוק את השיעור{' '}
                <strong className="text-gray-900">{deleteTarget.rate}%</strong>
                {' '}(תאריך תחולה: <strong>{formatDate(deleteTarget.effective_from)}</strong>)?
              </p>
              {activeRow?.id === deleteTarget.id && (
                <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 border
                                border-amber-200 rounded-lg text-xs text-amber-800">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
                  אזהרה: זהו השיעור הפעיל כרגע. מחיקתו עלולה להשאיר את המערכת ללא שיעור תקף.
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800
                           border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                ביטול
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700
                           disabled:opacity-50 disabled:cursor-not-allowed
                           text-white text-sm font-medium rounded-lg transition-colors"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                מחק
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ─── Exported helper ──────────────────────────────────────────────────────────

/**
 * Fetch the company's currently active VAT rate.
 *
 * Phase 15: queries `company_vat_history` for the latest row whose
 * `effective_from` is ≤ TODAY, falling back to DEFAULT_VAT (17) if the
 * table is empty or no row qualifies.
 *
 * Used by product form, pricelist display, and any document-generation
 * service that needs the live rate (NOT the snapshotted document rate).
 */
export async function fetchVatRate(companyId: string): Promise<number> {
  const today = new Date().toISOString().slice(0, 10);   // "YYYY-MM-DD"
  const { data } = await supabase
    .from('company_vat_history')
    .select('rate')
    .eq('company_id', companyId)
    .lte('effective_from', today)
    .order('effective_from', { ascending: false })
    .limit(1)
    .maybeSingle();
  return data ? Number((data as { rate: number }).rate) : DEFAULT_VAT;
}
